import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  input
} from '@angular/core';
import { PageComponent } from '../../layout/page/page.component';
import { TranslationService } from '../../../core/services/translation/translation.service';
import { formLanguageReloader } from '../../../core/helpers/page.helper';
import { LightColorContract } from './subscription.model';
import { Router } from '@angular/router';
import { ActionButtonEnum } from '../contract-action/action-list.model';
import { AuthenticationService } from '../../authentication/authentication.service';
import { ApplicationStore } from '../../../core/services/application-store.service';
import { RecordsService } from '../records/records.service';
import { filter, map, switchMap, tap } from 'rxjs';
import { RecordsResponse } from '../records/records.component.model';
import { LogService } from '../../../core/services/log/log.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ContractActionService } from '../contract-action/contract-action.service';
import { UserService } from '../../user-connection/user.service';
import { isUserPosWorklistRightPvmo, NoValue } from '@tribeauto/ngx-mobilite-material';
import { AppConfig } from '../../../app.config';
import { getParamsFromLeadsFilters } from '../../../core/helpers/url.helper';

@Component({
  selector: 'pvmo-subscription',
  imports: [PageComponent],
  templateUrl: './subscription.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SubscriptionComponent {
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly translationService = inject(TranslationService);
  private readonly router = inject(Router);
  private readonly authenticationService = inject(AuthenticationService);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly recordsService = inject(RecordsService);
  private readonly contractActionService = inject(ContractActionService);
  private readonly logService = inject(LogService);
  private readonly userService = inject(UserService);
  private readonly appConfig = inject(AppConfig);
  private readonly PAGE_NAME = 'page-subscription';
  private readonly LANDING = 'mobi-sub-landing';

  token = input.required<string>();
  externalId = input.required<string>();
  webStoreId = input.required<string>();
  isUserConnected = this.userService.isUserConnected;

  constructor() {
    effect(() => {
      const selected = this.translationService.selectedFile();
      formLanguageReloader(this.LANDING, this.PAGE_NAME, selected);
      this.cdr.markForCheck();
    });

    effect(() => {
      const userData = this.applicationStore.instant('userData')();
      let userSub: string | NoValue = undefined;
      if (this.appConfig.devOrLocal) {
        userSub = userData?.login;
      }
      this.createDetailComponent(userSub);
    });
  }

  private createDetailComponent(userSub: string | NoValue): void {
    const detailComponent = document.createElement(this.LANDING);

    detailComponent.setAttribute('nonce', 'NGINX_CSP_NONCE');
    detailComponent.setAttribute('language', this.translationService.selectedFile());
    detailComponent.setAttribute('external-id', this.externalId());
    if (userSub) {
      detailComponent.setAttribute('user-sub', userSub);
    }
    detailComponent.setAttribute('token', this.token());
    detailComponent.setAttribute('web-store-id', this.webStoreId());
    detailComponent.setAttribute('session-uuid', this.logService.sessionUuid());

    detailComponent.addEventListener('lightColorContract', event => {
      const lightColorContract: LightColorContract = (event as CustomEvent).detail;
      if (lightColorContract?.contractId) {
        this.contractActionService
          .fetchContractRecapListByNumAuto(lightColorContract.contractId)
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            map(contractResponse => contractResponse?.pointOfSale.vendorNumber),
            filter(Boolean),
            switchMap(vendorNumber =>
              this.recordsService.getFilesList(
                {
                  partnerInfo: {
                    webStoreIds: [vendorNumber]
                  },
                  userLogin: isUserPosWorklistRightPvmo(this.applicationStore.instant('userData')()?.userRights)
                    ? null
                    : this.applicationStore.instant('userData')()?.jossoLogin,
                  cckNumbers: [lightColorContract.contractId ?? ''],
                  caller: 'F'
                },
                0,
                1
              )
            ),
            filter(recordsResponse => !!recordsResponse && !!recordsResponse.myFileResponses[0]),
            switchMap((recordsResponse: RecordsResponse) => {
              const file = recordsResponse.myFileResponses[0];
              this.applicationStore.update('actionsData', { cckNumber: file.cckNumber, actions: file.actions });
              return this.authenticationService.generateInternalToken(file.cckNumber).pipe(
                takeUntilDestroyed(this.destroyRef),
                map(accessToken => {
                  return { accessToken, cckNumber: file.cckNumber };
                })
              );
            })
          )
          .subscribe(response => {
            this.applicationStore.update('webstoreData', { webstoreId: '' });
            void this.router.navigate([`/contract-action-hub/${response.cckNumber}/${ActionButtonEnum.DOCUMENTS}`], {
              queryParams: { token: response.accessToken, lightColor: lightColorContract.decision }
            });
          });
      }
    });

    detailComponent.addEventListener('backEvent', event => {
      const backEvent: boolean = (event as CustomEvent).detail;
      if (backEvent) {
        this.authenticationService
          .generateInternalToken(this.externalId())
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            tap(accessToken => {
              void this.router.navigate([`/simulation/${this.externalId()}`], {
                queryParams: { comingFromSub: 'true', token: accessToken }
              });
            })
          )
          .subscribe();
      }
    });

    detailComponent.addEventListener('backLeadsEvent', event => {
      const backLeadsEvent: boolean = (event as CustomEvent).detail;
      if (backLeadsEvent) {
        void this.router.navigate(['/leads'], {
          queryParams: getParamsFromLeadsFilters(this.applicationStore.instant('leadsFilters')())
        });
      }
    });

    const page = document.getElementById(this.PAGE_NAME);
    if (page) {
      page.append(detailComponent);
      this.cdr.markForCheck();
    }
  }
}
